import { useState } from "react";

import type { ReportScope } from "../../lib/api";
import { cn } from "../ui/cn";

export type { ReportScope };

/**
 * The date-range control every report card carries its own copy of.
 *
 * Per-card rather than one filter over the whole page, which is how the rest of
 * the Helpdesk already works: the questions a dashboard answers have different
 * natural
 * periods, and forcing "today" onto a twelve-month trend to see this morning's
 * SLA makes both useless at once.
 */

export const RANGE_PRESETS = [
  "today",
  "this-week",
  "last-week",
  "this-month",
  "last-month",
  "3-months",
  "12-months",
  "rolling",
  "custom",
] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number];

const LABELS: Record<RangePreset, string> = {
  today: "Today",
  "this-week": "This week",
  "last-week": "Last week",
  "this-month": "This month",
  "last-month": "Last month",
  "3-months": "3 months",
  "12-months": "12 months",
  rolling: "Rolling 30 days",
  custom: "Custom…",
};

export interface RangeValue {
  preset: RangePreset;
  /** ISO dates, only meaningful for the custom preset. */
  from?: string;
  to?: string;
}

/** Whether a custom range is complete enough to ask the server about. */
export function rangeIsReady(value: RangeValue): boolean {
  if (value.preset !== "custom") return true;
  return Boolean(value.from && value.to && value.from < value.to);
}

/**
 * A stored range, narrowed back to one this build knows about.
 *
 * The saved dashboard arrangement is JSON, so its preset is a bare string —
 * written by an older release, or by a colleague's shared template, or by
 * somebody editing a row. A preset that no longer exists falls back to the
 * card's default rather than being passed to the server, which would reject it
 * and leave the card permanently empty for that one person.
 */
export function toRangeValue(
  stored: { preset?: string; from?: string; to?: string } | undefined,
  fallback: RangeValue,
): RangeValue {
  const preset = RANGE_PRESETS.find((known) => known === stored?.preset);
  if (!preset) return fallback;
  return { preset, from: stored?.from, to: stored?.to };
}

export function RangeFilter({
  value,
  onChange,
}: {
  value: RangeValue;
  onChange: (value: RangeValue) => void;
}) {
  // Kept open once chosen, so correcting a date does not collapse the fields.
  const [showDates, setShowDates] = useState(value.preset === "custom");

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/*
        On a phone the select is laid invisibly over a face of our own, small
        enough for the half-width KPI tile it sits in -- a drawn select's
        "This month" hung out over the tile's edge. The select still takes the tap
        and opens the platform's own picker; it is only its text that is not
        drawn. A desktop sees the plain select, exactly as before.
      */}
      <span className="relative inline-flex rounded-md focus-within:ring-2 focus-within:ring-brand-500/40 md:focus-within:ring-0">
        <span
          aria-hidden
          className="flex h-6 items-center gap-1 rounded-md border border-line bg-surface px-1.5 text-[11px] text-content md:hidden"
        >
          {LABELS[value.preset]}
          <span className="text-[9px] text-faint">{"\u25be"}</span>
        </span>
        <select
          value={value.preset}
          onChange={(event) => {
            const preset = event.target.value as RangePreset;
            setShowDates(preset === "custom");
            onChange({ ...value, preset });
          }}
          aria-label="Date range"
          className={cn(
            "h-7 rounded-md border border-line bg-surface px-2 text-xs text-content",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            "max-md:absolute max-md:inset-0 max-md:h-full max-md:w-full max-md:opacity-0",
          )}
        >
          {RANGE_PRESETS.map((preset) => (
            <option key={preset} value={preset}>
              {LABELS[preset]}
            </option>
          ))}
        </select>
      </span>

      {showDates ? (
        <>
          <input
            type="date"
            value={value.from ?? ""}
            max={value.to}
            onChange={(event) => onChange({ ...value, from: event.target.value })}
            aria-label="From"
            className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
          />
          <span className="text-xs text-faint">to</span>
          <input
            type="date"
            value={value.to ?? ""}
            min={value.from}
            onChange={(event) => onChange({ ...value, to: event.target.value })}
            aria-label="To"
            className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
          />
        </>
      ) : null}
    </div>
  );
}

/** How long a duration in minutes reads to a person. */
export function formatMinutes(minutes: number | null): string {
  // Not "0m": an empty sample and an instant reply are different answers, and
  // only one of them is good news.
  if (minutes === null) return "—";
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${Math.round(minutes)}m`;

  const hours = minutes / 60;
  if (hours < 24) {
    const whole = Math.floor(hours);
    const rest = Math.round(minutes - whole * 60);
    return rest ? `${whole}h ${rest}m` : `${whole}h`;
  }

  const days = Math.floor(hours / 24);
  const restHours = Math.round(hours - days * 24);
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
}

/**
 * The arrow delta, in the one format used across every card.
 *
 * Direction is not always good news, so the caller says which way is up:
 * a rising SLA rate is an improvement, a rising response time is not.
 */
export function Delta({
  points,
  higherIsBetter = true,
  suffix = "pts",
}: {
  points: number | null;
  higherIsBetter?: boolean;
  suffix?: string;
}) {
  if (points === null || Math.abs(points) < 0.05) {
    return <span className="text-xs text-faint">—</span>;
  }

  const rising = points > 0;
  const good = rising === higherIsBetter;

  return (
    <span
      className={cn(
        "text-xs font-medium",
        good ? "text-sla-ok" : "text-sla-breached",
      )}
    >
      {rising ? "▲" : "▼"} {Math.abs(points).toFixed(1)}
      {suffix}
    </span>
  );
}
